import merge from 'lodash/merge';
import { makeASlightlyDifferentColor } from '../styleUtils';
import { DEFAULT_SETTINGS, MAIN_LAYOUT_SELECTORS, CSS_VARS } from '../constants';
const { darkBtnHoverColor, greyHoverColor, defaultBackgroundColor } = CSS_VARS;

/**
 * @description Create css styles for Primary Button Hover State Feature
 * @param hoverColor
 * @return {string}
 * @private
 */
function _getPrimaryBtnHoverStyles(designSettings) {
    const hoverColor = designSettings.general.primaryButtonHoverColor;
    if (!hoverColor) return '';

    return {
        '.btn:hover': { backgroundColor: hoverColor },
        '.btn--dark:hover': { backgroundColor: darkBtnHoverColor },
        '.btn--on-grey:hover': { backgroundColor: greyHoverColor },
        '.btn.btn--transp:hover': { backgroundColor: 'transparent' },
        '.btn--warning:hover': { backgroundColor: hoverColor },
        [MAIN_LAYOUT_SELECTORS]: {
            [`.btn--dark:hover,
        .btn--dark:hover.btn--on-grey`]: {
                backgroundColor: makeASlightlyDifferentColor(
                    designSettings.general.backgroundColor, 0.2, 0.3
                ),
            },
        },
    };
}

/**
 * @description Create css styles for Primary Button Text Feature
 * @param designSettings
 * @return {string}
 * @private
 */
function _getPrimaryBtnTextStyles(designSettings) {
    const { general, typography } = designSettings;
    const textColor = general.primaryButtonTextColor;
    if (!textColor) return '';

    return {
        '.btn': { color: textColor },
        '.sis-scope a.btn': {
            '&:hover': {
                color: textColor,
            },
            color: textColor,
        },
        '.btn--dark': { color: DEFAULT_SETTINGS.typography.primaryTextColor },
        '.btn--transp': { color: DEFAULT_SETTINGS.typography.secondaryTextColor },
        '.btn--white': { color: DEFAULT_SETTINGS.general.primaryButtonTextColor },
        '.btn--warning': { color: textColor },
        '.btn--warning.btn--disabled': { color: textColor },
        '.btn--warning[disabled=disabled]': { color: textColor },
        [MAIN_LAYOUT_SELECTORS]: {
            '.btn': { color: textColor },
            '.btn--white': { color: DEFAULT_SETTINGS.general.primaryButtonTextColor },
            '.btn--warning': { color: textColor },
            '.btn--transp': {
                color: typography.secondaryTextColor,
            },
            'a.btn': {
                '&:hover': {
                    color: textColor,
                },
                color: textColor,
            },
            'a.btn.btn--transp': {
                color: typography.secondaryTextColor,
                '&:hover': {
                    color: typography.primaryTextColor,
                },
            },
            '.btn--dark': { color: typography.primaryTextColor },
            'a.btn.btn--dark': {
                '&:hover': {
                    color: typography.primaryTextColor,
                },
                color: typography.primaryTextColor,
            },
        },
    };
}

/**
 * @description Create css styles for Secondary Button Color Feature
 * @param bgColor
 * @return {string}
 * @private
 */
function _getSecondaryBtnColorStyles(bgColor) {
    if (!bgColor) return '';

    return {
        '.btn--gray': { backgroundColor: bgColor },
        '.btn--gray.btn--disabled': { backgroundColor: bgColor },
        '.btn--gray[disabled=disabled]': { backgroundColor: bgColor },
        '.btn--gray.btn--disabled:hover': { backgroundColor: bgColor },
        '.btn--gray[disabled=disabled]:hover': { backgroundColor: bgColor },

        '.btn--smart': { backgroundColor: bgColor },
        '.btn--smart.btn--disabled': { backgroundColor: bgColor },
        '.btn--smart[disabled=disabled]': { backgroundColor: bgColor },
        '.btn--smart.btn--disabled:hover': { backgroundColor: bgColor },
        '.btn--smart[disabled=disabled]:hover': { backgroundColor: bgColor },
    };
}

/**
 * @description Create css styles for Secondary Button Hover State Feature
 * @param hoverColor
 * @return {string}
 * @private
 */
function _getSecondaryBtnHoverStyles(hoverColor) {
    if (!hoverColor) return '';

    return {
        '.btn--gray:hover': { background: hoverColor },
        '.btn--smart:hover': { background: hoverColor },
    };
}

/**
 * @description Create css styles for Secondary Button Text Feature
 * @param textColor
 * @return {string}
 * @private
 */
function _getSecondaryBtnTextStyles(textColor) {
    if (!textColor) return '';

    return {
        '.btn--gray': { color: textColor },
        '.btn.btn--gray': { color: textColor },
        '.btn--smart': { color: textColor },
    };
}

function _getPrimaryBtnBackStyle(backColor) {
    return {
        '.btn': { backgroundColor: backColor },
        '.btn--dark': { backgroundColor: 'transparent' },
        '.btn--transp': { backgroundColor: 'transparent' },
        '.btn--white': { backgroundColor: defaultBackgroundColor },

        '.btn.btn--disabled': { backgroundColor: backColor },
        '.btn.btn--disabled:hover': { backgroundColor: backColor },
        '.btn[disabled=disabled]': { backgroundColor: backColor },
        '.btn[disabled=disabled]:hover': { backgroundColor: backColor },

        '.btn--transp.btn--disabled': { backgroundColor: 'transparent' },
        '.btn--transp.btn--disabled:hover': { backgroundColor: 'transparent' },

        '.btn--dark.btn--disabled': { backgroundColor: 'transparent' },
        '.btn--dark[disabled=disabled]': { backgroundColor: 'transparent' },
        '.btn--dark.btn--disabled:hover': { backgroundColor: 'transparent' },
        '.btn--dark[disabled=disabled]:hover': { backgroundColor: 'transparent' },

        '.btn--warning': { backgroundColor: backColor },
        '.btn--warning.btn--disabled': { backgroundColor: backColor },
        '.btn--warning[disabled=disabled]': { backgroundColor: backColor },
    };
}

export default function (designSettings = {}) {
    const generalDesignSettings = designSettings.general || {};
    return merge(
        {},
        _getPrimaryBtnBackStyle(generalDesignSettings.brandColor),
        _getPrimaryBtnHoverStyles(designSettings),
        _getPrimaryBtnTextStyles(designSettings),
        _getSecondaryBtnColorStyles(generalDesignSettings.secondaryButtonBaseColor),
        _getSecondaryBtnHoverStyles(generalDesignSettings.secondaryButtonHoverColor),
        _getSecondaryBtnTextStyles(generalDesignSettings.secondaryButtonTextColor)
    );
}
